/**
 * useNotificationCta
 *
 * Runs the one action a notification's envelope carries. Every surface that
 * shows a notification — the header dropdown, the inbox page, a live toast —
 * calls this, so a button does the same thing wherever it is pressed and no
 * surface needs to know what kind of notification it is holding:
 *
 * - `navigate`  route inside the app
 * - `download`  open the file
 * - `generate`  run the report export the API built — streamed, queued or
 *               refused by the paywall, like any other export
 * - `respond`   answer an invitation in place, then retire the notification
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { NotificationUtils } from '@personal-trainer-app/shared';
import {
  notificationService,
  reportService,
} from '@personal-trainer-app/shared/src/services';

import type {
  NotificationItem,
  NotificationResponseOption,
  RawNotification,
} from '@personal-trainer-app/shared';

import { usePaywall } from '@/components/paywall';
import { useToast } from '@/components/ui/Toast';

export interface UseNotificationCtaOptions {
  /** Acting on a notification reads it. */
  onRead?: (id: string) => void;
  /** An answered invitation is spent; the surface drops it. */
  onResolved?: (id: string) => void;
}

export interface UseNotificationCtaReturn {
  /** The notification whose action is in flight, if any. */
  busyId: string | null;
  /**
   * Run the notification's action. `option` picks the button of a `respond`
   * action and is ignored by the rest. Resolves true when the action went
   * through.
   */
  run: (
    notification: NotificationItem | RawNotification,
    option?: NotificationResponseOption
  ) => Promise<boolean>;
}

/**
 * What the toast says once an invitation has been answered. The label is the
 * API's and may be anything, so it is quoted rather than conjugated.
 */
function answeredMessage(option: NotificationResponseOption): string {
  if (option.intent === 'decline') return 'Declined';

  return option.label === 'Accept' ? 'Accepted' : `${option.label} — done`;
}

export function useNotificationCta({
  onRead,
  onResolved,
}: UseNotificationCtaOptions = {}): UseNotificationCtaReturn {
  const navigate = useNavigate();
  const { success, info, error: showError } = useToast();
  const { showPaywall } = usePaywall();
  const [busyId, setBusyId] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    // Re-armed on mount: StrictMode runs this cleanup once before the real one.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(
    async (
      notification: NotificationItem | RawNotification,
      option?: NotificationResponseOption
    ): Promise<boolean> => {
      const action = NotificationUtils.getAction(notification);
      if (!action) return false;

      if (action.type === 'navigate' || action.type === 'download') {
        const target = NotificationUtils.getNotificationAction(notification);
        if (!target) return false;

        onRead?.(notification.id);
        if (action.type === 'download') {
          window.open(target, '_blank', 'noopener,noreferrer');
        } else {
          navigate(target);
        }
        return true;
      }

      setBusyId(notification.id);
      try {
        if (action.type === 'generate') {
          const result = await reportService.generateFromNotification(
            action.generate
          );

          if (result.type === 'queued') {
            info(result.message);
          } else {
            reportService.downloadFile(result.data, result.filename);
            success('Report downloaded');
          }
          onRead?.(notification.id);
          return true;
        }

        const chosen = option ?? action.options[0];
        if (!chosen) return false;

        await notificationService.respond(action, chosen);
        success(answeredMessage(chosen));
        onResolved?.(notification.id);
        return true;
      } catch (err) {
        if (!showPaywall(err)) {
          showError(
            err instanceof Error
              ? err.message
              : 'Something went wrong. Please try again.'
          );
        }
        return false;
      } finally {
        // The surface can go while its action is in flight — the dropdown
        // closed, the inbox left. The action still lands; its state goes too.
        if (mountedRef.current) setBusyId(null);
      }
    },
    [navigate, success, info, showError, showPaywall, onRead, onResolved]
  );

  return { busyId, run };
}

export default useNotificationCta;
